import React from 'react';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const DEFAULT_PALETTE = {
  surface: '#ffffff',
  text: '#111111',
  muted: '#666666',
  accent: '#4285f4',
  border: 'rgba(17, 17, 17, 0.12)',
  overlay: 'rgba(10, 10, 15, 0.82)',
  tagBg: 'rgba(66, 133, 244, 0.12)',
  tagText: '#4285f4'
};

export default function ContentPreviewModal({
  open,
  onClose,
  image,
  imageAlt,
  label,
  title,
  subtitle,
  description,
  ctaUrl,
  ctaLabel = 'Open Link',
  palette
}) {
  const colors = {
    ...DEFAULT_PALETTE,
    ...(palette || {})
  };

  if (!open) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3 },
          py: 3,
          backgroundColor: colors.overlay
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 'min(920px, 100%)',
            maxHeight: '90vh',
            overflow: 'hidden',
            borderRadius: '24px',
            backgroundColor: colors.surface,
            color: colors.text,
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.35)'
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close preview"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2,
              width: 44,
              height: 44,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              color: colors.text,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.14)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            {image && (
              <Box
                component="img"
                src={image}
                alt={imageAlt || title || 'Preview'}
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: { xs: '38vh', sm: '42vh' },
                  objectFit: 'cover',
                  backgroundColor: '#f3f4f6'
                }}
              />
            )}

            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              {(label || subtitle) && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  {label && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '999px',
                        backgroundColor: colors.tagBg,
                        color: colors.tagText,
                        fontSize: '12px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {label}
                    </Box>
                  )}
                  {subtitle && (
                    <Typography
                      sx={{
                        color: colors.muted,
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              )}

              {title && (
                <Typography
                  sx={{
                    fontSize: { xs: '28px', sm: '34px' },
                    lineHeight: 1.15,
                    fontWeight: 700,
                    mb: 2
                  }}
                >
                  {title}
                </Typography>
              )}

              {description && (
                <Typography
                  sx={{
                    fontSize: '16px',
                    lineHeight: 1.8,
                    color: colors.muted,
                    whiteSpace: 'pre-line'
                  }}
                >
                  {description}
                </Typography>
              )}

              {ctaUrl && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    component="a"
                    href={ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2.5,
                      py: 1.5,
                      borderRadius: '999px',
                      textDecoration: 'none',
                      backgroundColor: colors.accent,
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: 700,
                      '&:hover': {
                        opacity: 0.92
                      }
                    }}
                  >
                    {ctaLabel}
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
